import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useMatch } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import {
  Archive,
  ChevronDown,
  ChevronRight,
  Folder,
  ListChecks,
  Notebook,
  FileText,
  Brush,
} from 'lucide-react'
import { DocumentRowActions } from './DocumentRowActions'
import { FolderRowActions } from './FolderRowActions'
import { ListRowActions } from './ListRowActions'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { useExpandedFolder } from '../lib/space-prefs'
import {
  documentListResponse,
  folderListResponse,
  listListResponse,
} from '../lib/types'
import {
  dragId,
  dropFolderId,
  dropListId,
} from '../lib/sidebar-dnd'

export function SpaceChildren({ spaceId, workspaceId }) {
  const userId = useAuthStore((s) => s.user?.id)
  // Projects/folders are an accordion: closed by default, and opening one
  // collapses any other open project (a single expanded id, mirroring the
  // Space accordion above).
  const [expandedFolderId, setExpandedFolder] = useExpandedFolder(userId)

  const foldersQuery = useQuery({
    queryKey: ['folders', spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${spaceId}/folders`)
      return folderListResponse.parse(res.data)
    },
  })
  const listsQuery = useQuery({
    queryKey: ['lists', spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${spaceId}/lists`)
      return listListResponse.parse(res.data)
    },
  })
  const docsQuery = useQuery({
    queryKey: ['documents', spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${spaceId}/documents`)
      return documentListResponse.parse(res.data)
    },
  })

  const folders = foldersQuery.data?.data ?? []
  const lists = listsQuery.data?.data ?? []
  const allDocs = docsQuery.data?.data ?? []

  const grouped = useMemo(() => {
    const byFolder = new Map()
    const ensure = (k) => {
      let entry = byFolder.get(k)
      if (!entry) {
        entry = { lists: [], docs: [], archivedDocs: [] }
        byFolder.set(k, entry)
      }
      return entry
    }
    for (const l of lists) ensure(l.folderId ?? null).lists.push(l)
    for (const d of allDocs) {
      const bucket = ensure(d.folderId ?? null)
      if (d.isArchived) bucket.archivedDocs.push(d)
      else bucket.docs.push(d)
    }
    return byFolder
  }, [lists, allDocs])

  const root = grouped.get(null) ?? { lists: [], docs: [], archivedDocs: [] }
  const [showRootArchived, setShowRootArchived] = useState(false)

  // Lists + folders are the PRIMARY tree; documents load independently and
  // must NOT gate it. Previously ANY of the three queries still loading (or a
  // failed/ retrying docs query) returned null — so the expanded arrow showed
  // an empty void ("dropdown rotates but nothing opens"). Now the docs query
  // never blocks, and an expanded Space always renders a placeholder while its
  // lists load (or when it's genuinely empty).
  const primaryLoading = foldersQuery.isLoading || listsQuery.isLoading
  const hasAny =
    folders.length +
      root.lists.length +
      root.docs.length +
      root.archivedDocs.length >
    0
  if (primaryLoading || !hasAny) {
    return (
      <div className="ml-3 mt-0.5 border-l border-white/10 pl-2">
        <p className="px-3 py-1 text-sm italic text-[color:var(--color-muted)]">
          {primaryLoading ? 'Loading…' : 'No lists yet.'}
        </p>
      </div>
    )
  }

  return (
    <div className="ml-3 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
      {folders.map((f) => {
        const bucket = grouped.get(f.id) ?? { lists: [], docs: [], archivedDocs: [] }
        return (
          <FolderBlock
            key={`f-${f.id}`}
            folder={f}
            workspaceId={workspaceId}
            spaceId={spaceId}
            lists={bucket.lists}
            docs={bucket.docs}
            archivedDocs={bucket.archivedDocs}
            collapsed={expandedFolderId !== f.id}
            onToggle={() =>
              setExpandedFolder(expandedFolderId === f.id ? null : f.id)
            }
          />
        )
      })}

      {root.lists.map((l) => (
        <ListRow key={`l-${l.id}`} list={l} workspaceId={workspaceId} />
      ))}
      {root.docs.map((d) => (
        <DocRow key={`d-${d.id}`} doc={d} spaceId={spaceId} workspaceId={workspaceId} />
      ))}

      {root.archivedDocs.length > 0 ? (
        <>
          <button
            type="button"
            onClick={() => setShowRootArchived((v) => !v)}
            aria-expanded={showRootArchived}
            className="w-full flex items-center gap-1.5 mx-1 px-2 py-1 rounded-md text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-subtle)] hover:text-white hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            {showRootArchived ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <Archive size={12} className="shrink-0" />
            <span className="truncate flex-1 text-left">
              Archived ({root.archivedDocs.length})
            </span>
          </button>
          {showRootArchived
            ? root.archivedDocs.map((d) => (
                <DocRow
                  key={`a-${d.id}`}
                  doc={d}
                  spaceId={spaceId}
                  workspaceId={workspaceId}
                  archived
                />
              ))
            : null}
        </>
      ) : null}
    </div>
  )
}

function FolderBlock({
  folder,
  workspaceId,
  spaceId,
  lists,
  docs,
  archivedDocs,
  collapsed,
  onToggle,
}) {
  const dropData = { kind: 'folder', spaceId, folderId: folder.id }
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: dropFolderId(folder.id),
    data: dropData,
  })
  const [showArchived, setShowArchived] = useState(false)

  return (
    <div
      ref={setDropRef}
      className={
        'rounded-md transition-colors ' +
        (isOver
          ? 'bg-[color:var(--color-accent-bg)] outline outline-1 outline-[color:var(--color-accent-border)]'
          : '')
      }
    >
      <DraggableRow
        kind="folder"
        id={folder.id}
        fromSpaceId={folder.spaceId}
        fromFolderId={null}
        name={folder.name}
      >
        <ChildRowFrame onClick={onToggle}>
          {collapsed ? (
            <ChevronRight size={12} className="shrink-0 text-[color:var(--color-muted)]" />
          ) : (
            <ChevronDown size={12} className="shrink-0 text-[color:var(--color-muted)]" />
          )}
          <Folder size={14} className="shrink-0 text-white" />
          <span className="truncate flex-1" title={folder.name}>
            {folder.name}
          </span>
        </ChildRowFrame>
        <RowActionsSlot>
          <FolderRowActions folder={folder} workspaceId={workspaceId} />
        </RowActionsSlot>
      </DraggableRow>

      {!collapsed ? (
        <div className="ml-3 border-l border-white/10 pl-2">
          {lists.map((l) => (
            <ListRow key={`fl-${l.id}`} list={l} workspaceId={workspaceId} />
          ))}
          {docs.map((d) => (
            <DocRow key={`fd-${d.id}`} doc={d} spaceId={spaceId} workspaceId={workspaceId} />
          ))}
          {archivedDocs.length > 0 ? (
            <>
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                aria-expanded={showArchived}
                className="w-full flex items-center gap-1.5 mx-1 px-2 py-1 rounded-md text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-subtle)] hover:text-white hover:bg-[color:var(--color-card-hover)] cursor-pointer"
              >
                {showArchived ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                <Archive size={11} className="shrink-0" />
                <span className="truncate flex-1 text-left">
                  Archived ({archivedDocs.length})
                </span>
              </button>
              {showArchived
                ? archivedDocs.map((d) => (
                    <DocRow
                      key={`fa-${d.id}`}
                      doc={d}
                      spaceId={spaceId}
                      workspaceId={workspaceId}
                      archived
                    />
                  ))
                : null}
            </>
          ) : null}
          {lists.length === 0 && docs.length === 0 && archivedDocs.length === 0 ? (
            <p className="px-3 py-1 text-sm italic text-[color:var(--color-muted)]">
              Empty project.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function DraggableRow({ kind, id, fromSpaceId, fromFolderId, name, children }) {
  const data = { kind, id, fromSpaceId, fromFolderId, name }
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId(kind, id),
    data,
  })
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ touchAction: 'none' }}
      className={'group relative outline-none ' + (isDragging ? 'opacity-40' : '')}
    >
      {children}
    </div>
  )
}

function ListRow({ list, workspaceId }) {
  const listMatch = useMatch('/app/spaces/:spaceId/lists/:listId')
  const active = listMatch?.params?.listId === list.id
  const dropRef = useRef(null)
  const [position, setPosition] = useState('before')

  const dropData = {
    kind: 'list-reorder',
    spaceId: list.spaceId,
    listId: list.id,
    position,
  }
  const { setNodeRef: setDndDropRef, isOver } = useDroppable({
    id: dropListId(list.id),
    data: dropData,
  })

  function combinedRef(node) {
    dropRef.current = node
    setDndDropRef(node)
  }

  useEffect(() => {
    if (!isOver) return
    function onMove(e) {
      const rect = dropRef.current?.getBoundingClientRect()
      if (!rect) return
      setPosition(e.clientY < rect.top + rect.height / 2 ? 'before' : 'after')
    }
    window.addEventListener('pointermove', onMove)
    return () => window.removeEventListener('pointermove', onMove)
  }, [isOver])

  return (
    <DraggableRow
      kind="list"
      id={list.id}
      fromSpaceId={list.spaceId}
      fromFolderId={list.folderId ?? null}
      name={list.name}
    >
      <div ref={combinedRef} className="relative">
        {isOver && position === 'before' ? (
          <div
            className="absolute -top-px inset-x-1 h-[2px] bg-[color:var(--color-accent)] rounded pointer-events-none z-10"
            aria-hidden="true"
          />
        ) : null}
        <Link
          to={`/app/spaces/${list.spaceId}/lists/${list.id}`}
          title={list.name}
          className={
            // Mirrors the Company Admin sidebar's active List pill exactly
            // (AdminCurrentSpaceTree → ListLink) so the selected list looks
            // identical in both shells.
            'group flex items-center gap-2.5 mx-1 px-3 py-1.5 pr-8 rounded-[10px] text-sm cursor-pointer transition-colors ' +
            (active
              ? 'bg-white/10 text-white font-semibold border border-white/15'
              : 'text-white/65 hover:bg-white/5 hover:text-white border border-transparent')
          }
        >
          <ListChecks size={13} className="shrink-0 text-white/40" />
          <span className="truncate flex-1">{list.name}</span>
        </Link>
        {isOver && position === 'after' ? (
          <div
            className="absolute -bottom-px inset-x-1 h-[2px] bg-[color:var(--color-accent)] rounded pointer-events-none z-10"
            aria-hidden="true"
          />
        ) : null}
      </div>
      <RowActionsSlot>
        {/* "+ Add task" quick-add removed from the List row by
         *  request — the only remaining row action is the "..." menu.
         *  Tasks are created from inside the Space page itself. The
         *  `ListRowQuickAdd` component definition is also dropped
         *  below; re-adding it means restoring this child + the
         *  function. */}
        <ListRowActions list={list} workspaceId={workspaceId} />
      </RowActionsSlot>
    </DraggableRow>
  )
}

function DocRow({ doc, spaceId, workspaceId, archived }) {
  const Icon =
    doc.type === 'spreadsheet'
      ? FileText
      : doc.type === 'whiteboard'
      ? Brush
      : Notebook
  const iconClass =
    doc.type === 'spreadsheet'
      ? 'text-[color:var(--color-status-complete)]'
      : 'text-[color:var(--color-priority-normal)]'

  return (
    <DraggableRow
      kind="document"
      id={doc.id}
      fromSpaceId={doc.spaceId}
      fromFolderId={doc.folderId ?? null}
      name={doc.title}
    >
      <div className={archived ? 'opacity-60' : ''}>
        <Link
          to={`/app/documents/${doc.id}`}
          className="group flex items-center gap-2 mx-1 px-2 py-1 pr-8 rounded-md text-sm cursor-pointer transition-colors text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-white"
        >
          <Icon
            size={14}
            className={'shrink-0 text-[color:var(--color-ink-muted)] group-hover:text-white transition-colors ' + iconClass}
          />
          <span className="truncate flex-1">{doc.title}</span>
          {archived ? (
            <Archive
              size={11}
              className="shrink-0 text-[color:var(--color-ink-subtle)]"
              aria-label="Archived"
            />
          ) : null}
        </Link>
      </div>
      <RowActionsSlot>
        <DocumentRowActions document={doc} spaceId={spaceId} workspaceId={workspaceId} />
      </RowActionsSlot>
    </DraggableRow>
  )
}

function ChildRowFrame({ children, onClick }) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      onClick={onClick}
      // pr-8 reserves room for the absolutely-positioned RowActionsSlot kebab
      // (right-1) so the truncated name never runs underneath it — matching the
      // List / Doc rows.
      className="w-full flex items-center gap-2 mx-1 px-2 py-1 pr-8 rounded-md text-sm text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-white cursor-pointer"
    >
      {children}
    </div>
  )
}

function RowActionsSlot({ children }) {
  return (
    <div
      className="absolute right-1 top-1/2 -translate-y-1/2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-0.5"
      onPointerDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  )
}

// `ListRowQuickAdd` removed by request — the "+" quick-add icon no
// longer appears on List rows in the sidebar. The /lists/:id/tasks
// POST endpoint it called is unchanged and still drives the Create
// Task dialog inside the Space page.
